import { getTravelBookingsByEventRegistrations } from '../redux/travelCart/selectors';
import { REQUESTED_ACTION } from 'event-widgets/redux/modules/eventTravel';
import { isHotelRequestEnabled } from 'event-widgets/redux/selectors/eventTravel';
import * as currentRegistrant from '../redux/selectors/currentRegistrant';
import { getRegistrationTypeId, getSelectedAdmissionItem, getRegistrationPathId }
  from '../redux/registrationForm/regCart/selectors';
import { getIn } from 'icepick';
import { filterRoomRatesForBookingByUTCDates } from 'event-widgets/lib/HotelRequest/utils/HotelRequestUtil';
import { createSelector } from 'reselect';
import { getCurrencySymbol } from 'event-widgets/utils/travelUtils';
import { getAirActualsFromBooking } from '../redux/travelCart/airActuals';
import { SURVEY_TYPE } from 'event-widgets/utils/questionConstants';
import { openTravelUnsavedInfoWarningDialog } from '../dialogs';
import { getQuestionSurveyType } from './questionUtils';

/**
 * filter out non-cancelled entities
 * @type {function(*): boolean}
 */
const nonCancelledFilter = (entity => entity.requestedAction !== REQUESTED_ACTION.CANCEL);

/**
 * Utility function to return air booking for primary Registrant and it's guests.
 * @param {Object} state
 * @param {string} primaryEventRegistrationId
 * @param {Array} guestsEventRegistrationIds
 * @returns {Object}
 */
export function getAirBookingsToDisplay(state, primaryEventRegistrationId, guestsEventRegistrationIds) {
  const allBookings = getTravelBookingsByEventRegistrations(state,
    [primaryEventRegistrationId, ...guestsEventRegistrationIds]);
  return allBookings.reduce((accumulator, booking) => {
    if ( // don't need to display cancelled bookings
      booking.airBookings && (
        booking.requestedAction === REQUESTED_ACTION.BOOK || booking.requestedAction === REQUESTED_ACTION.MODIFY
      )
    ) {
      booking.airBookings
        .filter(a => a.requestedAction === REQUESTED_ACTION.BOOK || a.requestedAction === REQUESTED_ACTION.MODIFY)
        .forEach(airBooking => {
          // append the associated attendee with the booking
          if (booking.id === primaryEventRegistrationId) {
            accumulator.primary.push({ ...airBooking, attendeeRegistrationId: booking.id });
          } else {
            accumulator.guests.push({ ...airBooking, attendeeRegistrationId: booking.id });
          }
        });
    }
    return accumulator;
  }, { primary: [], guests: [] });
}

/**
 * Utility function to return airActuals for primary Registrant and it's guests.
 * Exclude cancelled bookings/airActuals/airActualFlights
 * @param {Object} state
 * @returns {Object}
 */
export const getAirActualsForCurrentRegistrant = (state, sourceTypes) => {
  const primaryEventRegistration = currentRegistrant.getEventRegistration(state) || {};
  const guestsEventRegistrations = currentRegistrant.getConfirmedGuests(state) || [];
  const currencyList = state.currencies ? Object.values(state.currencies) : [];

  const eventRegistrationIds = [primaryEventRegistration, ...guestsEventRegistrations].map(r => r.eventRegistrationId);
  const allBookings = getTravelBookingsByEventRegistrations(state, eventRegistrationIds);
  return allBookings
    .filter(nonCancelledFilter) // don't need to display cancelled bookings
    .reduce((accumulator, booking) => {
      getAirActualsFromBooking(booking, sourceTypes)
        .filter(nonCancelledFilter) // don't need to display cancelled air actuals
        .forEach(airActual => {
          let currencySymbol = getCurrencySymbol(currencyList, airActual.currencyId.toString());
          const updatedAirActual = {
            ...airActual,
            flightDetails: airActual.flightDetails.filter(nonCancelledFilter), // dont need cancelled flights
            attendeeRegistrationId: booking.id,
            currencySymbol
          };

          if (booking.id === primaryEventRegistration.eventRegistrationId) {
            accumulator.primary.push(updatedAirActual);
          } else {
            accumulator.guests.push(updatedAirActual);
          }
        });
      return accumulator;
    }, { primary: [], guests: [] });
};

/**
 * Utility function to return hotel room bookings for primary Registrant and it's guests.
 * @param {Object} state
 * @param {string} primaryEventRegistrationId
 * @param {Array} guestsEventRegistrationIds
 * @returns {Object}
 */
export function getHotelRoomBookingsToDisplay(state, primaryEventRegistrationId, guestsEventRegistrationIds) {
  const allBookings = getTravelBookingsByEventRegistrations(state,
    [primaryEventRegistrationId, ...guestsEventRegistrationIds]);
  return allBookings.reduce((accumulator, booking) => {
    if ( // don't need to display cancelled bookings
      booking.hotelRoomBookings && (
        booking.requestedAction !== REQUESTED_ACTION.CANCEL)
    ) {
      booking.hotelRoomBookings
        .filter(a => a.requestedAction !== REQUESTED_ACTION.CANCEL)
        .forEach(hotelRoomBooking => {
          // append the associated attendee with the booking
          if (booking.id === primaryEventRegistrationId) {
            accumulator.primary.push({ ...hotelRoomBooking, attendeeRegistrationId: booking.id });
          } else {
            accumulator.guests.push({ ...hotelRoomBooking, attendeeRegistrationId: booking.id });
          }
        });
    }
    return accumulator;
  }, { primary: [], guests: [] });
}

/**
 * Updates the reg types, admission items and reg paths from the reg cart into the corresponding travel bookings
 * @param {*} travelBookings travel bookings to update
 * @param {*} regCart reg cart object to update from
 */
export function updateRegistrantInformationInTravelBookings(travelBookings, regCart) {
  let bookings = [];
  let hasAnyBookingChanged = false;
  travelBookings.forEach(booking => {
    const { id, admissionItemId, registrationTypeId, registrationPathId } = booking;
    const regCartRegTypeId = getRegistrationTypeId(regCart, id);
    const regCartAdmissionItemId = (getSelectedAdmissionItem(regCart, id) || {}).productId;
    const regCartRegPathId = getRegistrationPathId(regCart, id);
    hasAnyBookingChanged = hasAnyBookingChanged ||
      (regCartRegTypeId !== registrationTypeId || regCartAdmissionItemId !== admissionItemId
        || regCartRegPathId !== registrationPathId);
    bookings.push({
      ...booking,
      admissionItemId: regCartAdmissionItemId || null,
      registrationTypeId: regCartRegTypeId,
      registrationPathId: regCartRegPathId
    });
  });
  return {
    hasAnyBookingChanged,
    bookings
  };
}

/**
 * Get credit card details
 * @param {Object} state travel bookings to update
 * @param {string} primaryEventRegistrationId reg cart object to update from
 * @returns {*}
 */
export function getCreditCardDetails(state, primaryEventRegistrationId) {
  const unSavedCreditCard = getIn(state, ['travelCart', 'userSession', 'creditCard']);
  if (!unSavedCreditCard) {
    const booking = (getTravelBookingsByEventRegistrations(state, [primaryEventRegistrationId]) || [])[0];
    const contextId = getIn(booking, ['creditCard', 'contextId']);
    const creditCardDetails = getIn(booking, ['creditCard', 'creditCardDetails'])
      ? { ...booking.creditCard.creditCardDetails } : {};
    return contextId ? { ...booking.creditCard.creditCardDetails, contextId }
      : creditCardDetails;
  }
  return unSavedCreditCard;
}

/**
 * Returns whether any type of booking / credit card is present in the travel cart
 */
export function hasAnyTravelItem(travelForm) {
  const { userSession } = travelForm;
  return hasAnyValidBooking(travelForm) || !!getIn(userSession, ['creditCard', 'number']);
}

/**
 * Whether the travel cart has any bookings that need to be processed by the backend
 */
export const hasAnyBookingsToProcess = (travelCart, eventTravel) => {
  const { cart } = travelCart;
  return cart && cart.bookings && cart.bookings.length > 0 &&
    cart.bookings.some(booking => booking &&
      ((booking.hotelRoomBookings && booking.hotelRoomBookings.length > 0 && isHotelRequestEnabled(eventTravel))
      || (booking.airBookings && booking.airBookings.length > 0)
      || (booking.airActuals && booking.airActuals.length > 0)));
};

/**
 * Returns whether any type of booking is present in the travel cart
 */
function hasAnyValidBooking(travelForm) {
  const { cart } = travelForm;
  return cart && cart.bookings && cart.bookings.length > 0 &&
    cart.bookings.some(booking =>
      booking && ((booking.hotelRoomBookings && booking.hotelRoomBookings.length > 0) ||
        (booking.airBookings && booking.airBookings.length > 0) ||
        (booking.airActuals && booking.airActuals.length > 0)));
}

/**
 * Returns whether any travel booking is present for invitee and it's guests.
 * @param {Object} state
 * @param {string} primaryEventRegistrationId
 * @param {Array} guestsEventRegistrationIds
 * @returns {boolean}
 */
export function hasAnyTravelBookingForInviteeAndGuests(state, primaryEventRegistrationId, guestsEventRegistrationIds) {
  // check for air requests
  const airBookings = getAirBookingsToDisplay(state, primaryEventRegistrationId, guestsEventRegistrationIds);
  if (airBookings.primary.length > 0 || airBookings.guests.length > 0) return true;

  // check for hotel requests
  const hotelRoomBookings = getHotelRoomBookingsToDisplay(state, primaryEventRegistrationId,
    guestsEventRegistrationIds);
  return (hotelRoomBookings.primary.length > 0 || hotelRoomBookings.guests.length > 0);
}

/**
 * Utility function to check if any hotel room bookings exists
 * for which the attendee has to pay during the registration.
 * @param {Object} state
 * @param {string} primaryEventRegistrationId
 * @param {Array} guestsEventRegistrationIds
 * @returns {Object}
 */
export function hasAnyPaidHotelRoomBookings(state, primaryEventRegistrationId, guestsEventRegistrationIds, hotels) {
  const allBookings = getTravelBookingsByEventRegistrations(state, [primaryEventRegistrationId,
    ...guestsEventRegistrationIds]);
  let hasAnyPaidBooking = false;
  allBookings.forEach(booking => {
    if (!hasAnyPaidBooking && booking.hotelRoomBookings && (booking.requestedAction !== REQUESTED_ACTION.CANCEL)) {
      booking.hotelRoomBookings.forEach(hotelBooking => {
        if (!hasAnyPaidBooking && hotelBooking.requestedAction !== REQUESTED_ACTION.CANCEL) {
          // check if the booking has rate > 0
          const roomTypes = getIn(hotels.find(h => h.id === hotelBooking.hotelId), ['roomTypes']);
          const roomRates = getIn(roomTypes.find(r => r.id === hotelBooking.roomTypeId), ['roomRate']);

          // get planner confirmed dates if planner fields exists
          const checkinDate = (hotelBooking.plannerFields && hotelBooking.plannerFields.plannerConfirmedCheckinDate)
            || hotelBooking.confirmedCheckinDate || hotelBooking.checkinDate;
          const checkoutDate = (hotelBooking.plannerFields && hotelBooking.plannerFields.plannerConfirmedCheckoutDate)
            || hotelBooking.confirmedCheckoutDate || hotelBooking.checkoutDate;

          const applicableRates = filterRoomRatesForBookingByUTCDates(roomRates, checkinDate,
            checkoutDate, booking.registrationTypeId);
          if (applicableRates.find(r => r.rate > 0)) {
            hasAnyPaidBooking = true;
          }
        }
      });
    }
  });
  return hasAnyPaidBooking;
}

/**
 * Utility function to return air actuals for primary Registrant and it's guests.
 * @param {Object} state
 * @param {string} primaryEventRegistrationId
 * @param {Array} guestsEventRegistrationIds
 * @returns {Object}
 */
export function getAirActualsToDisplay(state, primaryEventRegistrationId, guestsEventRegistrationIds) {
  const allBookings = getTravelBookingsByEventRegistrations(state,
    [primaryEventRegistrationId, ...guestsEventRegistrationIds]);
  const currencyList = state.currencies ? Object.values(state.currencies) : [];
  return allBookings.reduce((accumulator, booking) => {
    // don't need to display cancelled bookings
    booking.airActuals
      .filter(nonCancelledFilter) // don't need to display cancelled air actuals
      .forEach(airActual => {
        const updatedAirActual = {
          ...airActual,
          flightDetails: airActual.flightDetails.filter(nonCancelledFilter), // dont need cancelled flights
          attendeeRegistrationId: booking.id,
          // append currency symbol
          currencySymbol: getCurrencySymbol(currencyList, airActual.currencyId.toString())
        };

        if (booking.id === primaryEventRegistrationId) {
          accumulator.primary.push(updatedAirActual);
        } else {
          accumulator.guests.push(updatedAirActual);
        }
      });
    return accumulator;
  }, { primary: [], guests: [] });
}

/**
 * Utility function to get currency list with details like currency_symbol, iso_code
 * @param {Object} state
 * @returns {Object}
 */
export const getCurrencyDetailsForAirActuals = createSelector(
  state => getIn(state, ['account', 'accountCurrencies']) || [],
  state => state.currencies,
  (accountCurrencies, allCurrencies) => {
    let currencyList = [];
    accountCurrencies.forEach(accountCurrency => {
      let currencyDetails = allCurrencies[accountCurrency.id.toString()];
      currencyList.push({
        ...accountCurrency,
        iSOCode: currencyDetails.iSOCode,
        nameOfSymbol: currencyDetails.nameOfSymbol,
        symbol: currencyDetails.symbol
      });
    });
    return currencyList;
  }
);

/**
 * validates if we have travel widgets unsaved info on current page and navigate
 * @param handler
 * @returns {Promise<*>}
 */
export async function validateTravelWidgetsAndNavigate(handler, dispatch, state) {
  const userTravelSessionInfo = getIn(state, ['travelCart', 'userSession']);
  // if travel user session exists
  if (userTravelSessionInfo) {
    const hotelRequestSessionInfo = userTravelSessionInfo.hotelRequest;
    const hasIncompleteAirRequestInfo = !getIn(userTravelSessionInfo, ['airRequest', 'showSummary']);
    const hasIncompleteAirActualInfo = !getIn(userTravelSessionInfo, ['airActual', 'showSummary']);
    const hasIncompleteHotelRequestInfo = !hotelRequestSessionInfo.isSummaryView &&
      hotelRequestSessionInfo.expandedHotels && hotelRequestSessionInfo.expandedHotels.length > 0;

    if (hasIncompleteAirRequestInfo || hasIncompleteAirActualInfo || hasIncompleteHotelRequestInfo) {
      return await dispatch(openTravelUnsavedInfoWarningDialog(handler, state.text.translate));
    }
  }
  await handler();
}

/**
 * Returns travel questions data to be displayed
 * @param {Object} state
 * @param {Object} props
 * @returns {Array}
 */
export function getTravelQuestionsData(state, props) {
  const surveyType = getQuestionSurveyType(props.config);
  const { config: { appData: { parentQuestionId } } } = props;
  const primaryEventRegistrationId = (currentRegistrant.getEventRegistration(state) || {}).eventRegistrationId;
  const guestsEventRegistrationIds = (currentRegistrant.getConfirmedGuests(state) || [])
    .map(r => r.eventRegistrationId);

  switch (surveyType) {
    case SURVEY_TYPE.HOTEL_QUESTIONS: {
      const hotelBookings =
        getHotelRoomBookingsToDisplay(state, primaryEventRegistrationId, guestsEventRegistrationIds);
      let allBookings = [...hotelBookings.primary, ...hotelBookings.guests];
      // If child question, we need to show it only once and for only parent booking id
      if (parentQuestionId) {
        allBookings = allBookings.filter((booking) => {
          return booking.id === props.metaData.bookingId;
        });
      }
      const questions = allBookings.map((booking) => {
        return { booking };
      });
      return questions;
    }
    case SURVEY_TYPE.HOTEL_ALTERNATE_QUESTIONS: {
      const hotelBookings =
        getHotelRoomBookingsToDisplay(state, primaryEventRegistrationId, guestsEventRegistrationIds);
      let allBookings = [...hotelBookings.primary, ...hotelBookings.guests];

      if (!allBookings.length) {
        return [{
          booking: {
            id: '00000000-0000-0000-0000-000000000000'
          }
        }];
      }
      return [];
    }
    default:
      return [];
  }
}
